import React from 'react';
import { useAuth } from '../Auth/AuthProvider';
import './LandingPage.css';

const LandingPage = () => {
  const { user } = useAuth();

  const handleGetStarted = () => {
    if (user) {
      // ログイン済みの場合はクイズ一覧へ
      window.location.href = '/';
    } else {
      // 未ログインの場合は登録画面へ
      window.location.href = '/?tab=signup';
    }
  };

  const handleViewPublicQuizzes = () => {
    window.location.href = '/public';
  };

  return (
    <div className="landing-page">
      {/* ヘッダー */}
      <header className="landing-header" role="banner">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img src="/apple-touch-icon.png" alt="Quizenie（クイジーニー）のアイコン - 学習クイズアプリケーション" className="logo-icon" />
              <h1 itemProp="name">Quizenie</h1>
            </div>
            <nav className="nav-links" role="navigation" aria-label="メインメニュー">
              <a href="#features" aria-label="機能セクションへ移動">機能</a>
              <a href="#how-it-works" aria-label="使い方セクションへ移動">使い方</a>
              <a href="#pricing" aria-label="料金セクションへ移動">料金</a>
            </nav>
            <div className="header-buttons">
              {!user && (
                <div className="auth-buttons">
                  <button className="login-btn" onClick={() => window.location.href = '/?tab=login'}>
                    ログイン
                  </button>
                  <button className="signup-btn" onClick={() => window.location.href = '/?tab=signup'}>
                    アカウント作成
                  </button>
                </div>
              )}
              {user && (
                <button className="dashboard-btn" onClick={() => window.location.href = '/'}>
                  クイズ一覧
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ヒーローセクション */}
      <section className="hero-section" role="main" itemScope itemType="https://schema.org/SoftwareApplication">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title" itemProp="name">
                学習を楽しく<br />
                効果的に<br />
                <span className="highlight">Quizenie</span>で始めよう
              </h1>
              <p className="hero-description" itemProp="description">
                クイズを作成・みんなに共有しよう。<br />
                直感的な操作性で、学習体験を変革します。<br />
                AIでクイズ作成できる新しい学びの形。
              </p>
              <div className="hero-buttons">
                <button className="cta-primary" onClick={handleGetStarted}>
                  {user ? 'クイズを始める' : '無料で始める'}
                </button>
                <button className="cta-secondary" onClick={handleViewPublicQuizzes}>
                  公開クイズを見る
                </button>
              </div>
            </div>
            <div className="hero-image">
              <img src="/images/landing/hero-screenshot.png" alt="Quizenie（クイジーニー）メイン画面のスクリーンショット - クイズ作成と学習管理機能を表示" itemProp="screenshot" />
            </div>
          </div>
        </div>
      </section>

      {/* 機能セクション */}
      <section id="features" className="features-section" role="region" aria-labelledby="features-title">
        <div className="container">
          <h2 id="features-title" className="section-title">なぜQuizenieを選ぶのか？</h2>
          <div className="features-grid" itemScope itemType="https://schema.org/ItemList">
            <div className="feature-card" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <div className="feature-icon">
                <span className="icon" role="img" aria-label="編集アイコン">✏️</span>
              </div>
              <h3 itemProp="name">簡単クイズ作成</h3>
              <p itemProp="description">直感的なインターフェースで、誰でも数分でプロ品質のクイズを作成できます。</p>
            </div>
            <div className="feature-card" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <div className="feature-icon">
                <span className="icon" role="img" aria-label="AIロボットアイコン">🤖</span>
              </div>
              <h3 itemProp="name">AI自動生成</h3>
              <p itemProp="description">テキストや画像からAIが自動でクイズを生成。効率的な学習コンテンツ作成をサポート。</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <span className="icon">🔗</span>
              </div>
              <h3>簡単共有</h3>
              <p>作成したクイズをワンクリックで共有。チームや友達と一緒に学習を楽しめます。</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <span className="icon">📊</span>
              </div>
              <h3>詳細分析</h3>
              <p>学習進捗を詳細に分析。効果的な学習計画を立てることができます。</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <span className="icon">📱</span>
              </div>
              <h3>モバイル対応</h3>
              <p>スマートフォンでもPCでも、いつでもどこでも学習を続けられます。</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <span className="icon">⚙️</span>
              </div>
              <h3>豊富なカスタマイズ</h3>
              <p>問題数、出題順など、学習スタイルに合わせて細かく設定できます。</p>
            </div>
          </div>
        </div>
      </section>

      {/* 使い方セクション */}
      <section id="how-it-works" className="how-it-works-section" role="region" aria-labelledby="how-it-works-title">
        <div className="container">
          <h2 id="how-it-works-title" className="section-title">3ステップで始める</h2>
          <div className="steps-container" itemScope itemType="https://schema.org/HowTo">
            <div className="step" itemProp="step" itemScope itemType="https://schema.org/HowToStep">
              <div className="step-number" itemProp="position">1</div>
              <div className="step-content">
                <h3 itemProp="name">アカウント作成</h3>
                <p itemProp="text">メールアドレスで簡単登録。すぐに使い始められます。</p>
                <img src="/images/landing/step-1-signup.png" alt="アカウント作成画面のスクリーンショット - メールアドレス入力フォーム" itemProp="image" />
              </div>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>クイズ作成</h3>
                <p>直感的なエディターでクイズを作成。AIの力も借りられます。</p>
                <img src="/images/landing/step-2-create.png" alt="クイズ作成" />
              </div>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>学習開始</h3>
                <p>作成したクイズで学習開始。進捗を確認しながら効率的に学習。</p>
                <img src="/images/landing/step-3-learn.png" alt="学習開始" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 料金セクション */}
      <section id="pricing" className="pricing-section">
        <div className="container">
          <h2 className="section-title">シンプルな料金体系</h2>
          <div className="pricing-cards">
            <div className="pricing-card free">
              <h3>無料プラン</h3>
              <div className="price">
                <span className="currency">¥</span>
                <span className="amount">0</span>
                <span className="period">/月</span>
              </div>
              <ul className="features-list">
                <li>クイズ作成 最大10個</li>
                <li>基本的な分析機能</li>
                <li>公開クイズへのアクセス</li>
                <li>モバイル対応</li>
              </ul>
              <button className="pricing-btn" onClick={handleGetStarted}>
                無料で始める
              </button>
            </div>
            <div className="pricing-card premium">
              <div className="popular-badge">期間限定</div>
              <h3>プレミアムプラン</h3>
              <div className="price">
                <div className="current-price">
                  <span className="currency">¥</span>
                  <span className="amount">0</span>
                  <span className="period">/月</span>
                </div>
              </div>
              <ul className="features-list">
                <li>無制限のクイズ作成</li>
                <li>優先クイズアクセス</li>
                <li>AIクイズ自動生成機能</li>
                <li>詳細分析とレポート</li>
                <li>クイズ共有機能</li>
              </ul>
              <button className="pricing-btn premium-btn" onClick={handleGetStarted}>
                プレミアムを始める
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTAセクション */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>今すぐ学習を始めませんか？</h2>
            <p>Quizenieで効果的な学習体験を手に入れましょう。</p>
            <button className="cta-button" onClick={handleGetStarted}>
              {user ? 'クイズを始める' : '無料で始める'}
            </button>
          </div>
        </div>
      </section>

      {/* フッター */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <img src="/apple-touch-icon.png" alt="Quizenie Icon" />
                <span>Quizenie</span>
              </div>
            </div>
            <div className="footer-section">
              <h4>サポート</h4>
              <ul>
                <li><a href="https://x.com/_Quizenie_">お問い合わせ(X)</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Quizenie. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;